import React, { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import workvivoService from "../../services/workvivoService";

// Admin widget for picking the org's default Workvivo space (used as the
// pre-selected option when a user posts a kudo from a recognition).
const WorkvivoDefaultSpaceCard = () => {
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      workvivoService.getIntegration(),
      workvivoService.getSpaces(),
    ])
      .then(([integ, spacesResp]) => {
        if (cancelled) return;
        setIntegration(integ);
        const spaceList = spacesResp?.data || spacesResp || [];
        setSpaces(spaceList);
        setSelected(integ?.default_space_id || "");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Failed to load Workvivo integration");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const updated = await workvivoService.updateIntegration({
        default_space_id: selected || null,
      });
      setIntegration(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || "Failed to update integration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading Workvivo integration...
      </div>
    );
  }

  if (!integration) {
    return null; // No integration configured for this org — nothing to render.
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
          <Star className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Default Workvivo Space
          </h3>
          <p className="text-xs text-slate-500">
            Pre-selected when users cross-post a recognition as a kudo. They can
            still change it per recognition.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">— None (default to global) —</option>
          {spaces.map((s) => (
            <option key={s.external_space_id} value={s.external_space_id}>
              {s.name}
              {s.visibility ? ` · ${s.visibility}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-emerald-500 text-white rounded-full text-sm hover:bg-emerald-600 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {savedAt && (
        <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Saved
        </p>
      )}
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
};

export default WorkvivoDefaultSpaceCard;
